import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import styled from '@emotion/styled';
import { useSelector } from '~Hooks';
import { InstanceState } from '..';
import Tab from './Tab';
import NewTabButton from './NewTabButton';

export const Wrapper = styled.div`
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2;
  height: 80px;
  flex-direction: row;
  justify-content: start;
  align-items: flex-end;
  width: 100vw;
  padding: 0 2px;
  box-sizing: border-box;
  background: var(--colorBlueGray1300);
  overflow: hidden;
  border-bottom: 2px solid var(--colorBlueGray1300);
`;
interface Props {
  instances: InstanceState[];
  currentIndex: number;
  onNewTab: () => void;
  onCloseTab: (id: number) => void;
  onTabClicked: (index: number) => void;
}

const TabBar: React.FunctionComponent<Props> = ({
  currentIndex,
  onTabClicked,
  onNewTab,
  onCloseTab,
  instances,
}) => {
  const payments = useSelector((state) => state.POSPayments);

  /**
   * FAILED PAYMENTS
   * We store failed payments here so that even after the payment
   * itself has been removed we can show a "failed" warning to
   * the staff until we are sure they have looked at it
   */
  const [failedPayments, setFailedPayments] = useState<number[]>([]);

  /**
   * Remove failed payments when we visit its corresponding tab
   */
  useEffect(() => {
    // Filter all saved failures for the current tab
    const filtered = failedPayments.filter((fp) => {
      const index = instances.findIndex((i) => i.id === fp);

      return index !== currentIndex;
    });

    setFailedPayments(filtered);
  }, [currentIndex]);

  /**
   * Add a failure to a more long-lived state when a payment
   * in a tab other than the active one fails
   */
  useEffect(() => {
    // Get all failed payments
    const failed = payments.filter((p) => p.status === 'FAILED');

    // Find all failed payments which belong to an instance which
    // is not the currently visible one
    const filtered = failed.reduce((arr, payment) => {
      const index = instances.findIndex((i) => i.orderId === payment.order);

      if (index !== currentIndex && instances[index]) {
        arr.push(instances[index].id);
      }

      return arr;
    }, failedPayments);

    // This will definitely add duplicates over time, so clear it out
    setFailedPayments(_.uniq(filtered));
  }, [payments]);

  return (
    <Wrapper>
      {instances.map((instance, index) => {
        const payment = payments.find((p) => p.order === instance.orderId);

        const isFailed = failedPayments.some((id) => id === instance.id);

        const paymentStatus = isFailed ? 'FAILED' : payment?.status;

        const isActive = index === currentIndex;

        return (
          <Tab
            onTabClicked={() => onTabClicked(index)}
            onCloseTab={() => onCloseTab(instance.id)}
            paymentStatus={paymentStatus}
            id={instance.id}
            isActive={isActive}
            payment={payment}
            key={instance.id}
          />
        );
      })}
      {instances.length < 5 && <NewTabButton onNewTab={onNewTab} />}
    </Wrapper>
  );
};

export default TabBar;
